import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { ColorSwatch } from "../color-swatch";
import { styles } from "./style";

interface Color {
	value: string;
	label: string;
}

// v9 renders only these named legacy colours, so v9 keeps a set list rather than v10's free hex
// (CSS panel 2 sticky; decided 2026-09-28). Moved from the old Version Settings page, unchanged.
const colors: Color[] = [
	{ value: "#999999", label: "Gray" },
	{ value: "#d9534f", label: "Red" },
	{ value: "#5cb85c", label: "Green" },
	{ value: "#f0ad4e", label: "Orange" },
	{ value: "#428bca", label: "Blue" },
	{ value: "#5bc0de", label: "Light Blue" },
	{ value: "#006dcc", label: "Blue Gradient" },
];

interface Props extends WithStyles<typeof styles> {
	id: string;
	label: string;
	value: string;
	/** The colour's validation message, shown under the field (MAX-4577). */
	error?: string;
	onChange: (value: string) => void;
}

const Component: React.FC<Props> = ({ id, label, value, error, onChange, classes = {} }) => {
	const named = colors.find((color) => color.value === value);
	// A colour outside the set, e.g. a hex saved while the client was on v10, is kept as "Custom"
	// until another colour is picked, rather than showing a blank field.
	const selected = named || (value ? { value, label: "Custom" } : undefined);
	return (
		<Mui.FormControl fullWidth variant="outlined" error={!!error}>
			<Mui.Typography component="label" id={`${id}-label`} className={classes.fieldLabel}>
				{label}
			</Mui.Typography>
			<Mui.Select
				id={id}
				labelId={`${id}-label`}
				value={selected ? selected.value : ""}
				onChange={(event) => onChange(event.target.value as string)}
				// The field already carries the swatch, so show the name alone rather than the menu row.
				renderValue={() => (selected ? selected.label : "")}
				displayEmpty
				classes={{ icon: classes.selectIcon }}
				IconComponent={Icons.ChevronDown}
				input={
					<Mui.OutlinedInput
						startAdornment={<ColorSwatch color={selected ? selected.value : "transparent"} />}
						classes={{
							root: `${classes.selectInputRoot} ${classes.inputRoot}`,
							focused: classes.selectFocused,
							notchedOutline: classes.selectNotchedOutline,
							input: classes.selectInput,
						}}
					/>
				}
			>
				{selected && !named && (
					<Mui.MenuItem value={selected.value} disabled>
						<span className={classes.menuSwatch} style={{ backgroundColor: selected.value }} />
						{selected.label}
					</Mui.MenuItem>
				)}
				{colors.map((color) => (
					<Mui.MenuItem key={color.value} value={color.value}>
						<span className={classes.menuSwatch} style={{ backgroundColor: color.value }} />
						{color.label}
					</Mui.MenuItem>
				))}
			</Mui.Select>
			{error && <Mui.FormHelperText id={`${id}-error`}>{error}</Mui.FormHelperText>}
		</Mui.FormControl>
	);
};

export const PrimaryColorDropdown = withStyles(Component, styles);
